/**
 * SeriesReorderPanel — réordonne les œuvres d'une série par drag-and-drop.
 *
 * Affiché sous le champ « Série » dans l'éditeur d'œuvre dès qu'une série
 * est sélectionnée. L'œuvre courante (`currentWorkId`) est mise en avant
 * dans la liste.
 *
 * Optimistic update : on bascule l'ordre local immédiatement au drop puis
 * on appelle `useReorderSeries`. Si le BE rejette (ex: race avec une autre
 * fenêtre qui a modifié les membres), on rollback via invalidation.
 */

import { useMemo, useState } from 'react'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import MediaImage from '@/components/ui/MediaImage'
import { useReorderSeries, useSeriesWorks } from '@/hooks/useWorks'
import type { SeriesWorkSummary } from '@/types/works'

import './SeriesReorderPanel.css'

interface SeriesReorderPanelProps {
  seriesId: number
  seriesName: string
  currentWorkId: number | null
}

export default function SeriesReorderPanel({
  seriesId,
  seriesName,
  currentWorkId,
}: SeriesReorderPanelProps) {
  const query = useSeriesWorks(seriesId)
  const reorder = useReorderSeries(seriesId)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // Override local actif uniquement pendant un drag in-flight : on
  // affiche l'ordre optimiste tant que la mutation tourne, puis on
  // retombe sur `query.data` (rafraîchi via `onSuccess` du hook).
  const [dragOverride, setDragOverride] = useState<SeriesWorkSummary[] | null>(null)

  const items = useMemo<SeriesWorkSummary[]>(
    () => dragOverride ?? query.data ?? [],
    [dragOverride, query.data],
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = items.findIndex((it) => it.id === active.id)
    const newIndex = items.findIndex((it) => it.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return

    const reordered = arrayMove(items, oldIndex, newIndex)
    setDragOverride(reordered)

    reorder.mutate(
      { ordered_work_ids: reordered.map((it) => it.id) },
      {
        onSuccess: () => setDragOverride(null),
        onError: () => {
          setDragOverride(null)
          toast.error("Réordonnancement impossible — restauration de l'ordre précédent.")
        },
      },
    )
  }

  if (query.isLoading) {
    return (
      <div className="series-reorder-panel series-reorder-panel--loading" aria-busy="true">
        Chargement des œuvres de la série…
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className="series-reorder-panel series-reorder-panel--error">
        Impossible de charger les œuvres de « {seriesName} ».
      </div>
    )
  }

  if (items.length === 0) {
    return null
  }

  return (
    <div className="series-reorder-panel">
      <header className="series-reorder-panel-header">
        <h3 className="series-reorder-panel-title">
          Œuvres de la série « {seriesName} »{' '}
          <span className="series-reorder-panel-count">{items.length}</span>
        </h3>
        <p className="series-reorder-panel-help">
          Glisser pour réordonner. La position est enregistrée automatiquement.
        </p>
      </header>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
          <ol className="series-reorder-list">
            {items.map((item, idx) => (
              <SortableRow
                key={item.id}
                item={item}
                rank={idx + 1}
                isCurrent={item.id === currentWorkId}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>
    </div>
  )
}

interface SortableRowProps {
  item: SeriesWorkSummary
  rank: number
  isCurrent: boolean
}

function SortableRow({ item, rank, isCurrent }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const className = [
    'series-reorder-row',
    isCurrent && 'series-reorder-row--current',
    isDragging && 'series-reorder-row--dragging',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <li ref={setNodeRef} style={style} className={className}>
      <Button
        type="button"
        variant="quiet"
        size="sm"
        className="series-reorder-handle"
        aria-label={`Déplacer ${item.title_fr}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} aria-hidden="true" />
      </Button>
      <span className="series-reorder-rank">{rank}.</span>
      <MediaImage
        minioKey={item.thumb_minio_key}
        alt={item.title_fr}
        className="series-reorder-thumb"
      />
      <span className="series-reorder-title">{item.title_fr}</span>
      {isCurrent && <span className="series-reorder-badge">Cette œuvre</span>}
    </li>
  )
}
